import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Platform, StatusBar, useWindowDimensions} from 'react-native';
import {NavigationContainer, Theme, useNavigationContainerRef} from '@react-navigation/native';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import {track} from '../services/analytics';
import {SplashScreen} from '../screens/Common/Splash';
import LoginWithPin from "../screens/LoginWithPin";
import CancelModal from "../screens/Auth/SignUp/steps/CancelModal";
import {StoreContext} from "./StoreContextProvider";
import Loader from "./Loader";
import BetSlip from "../screens/User/BetSlip";
import Account from "../screens/User/Account";
import * as NavigationBar from 'expo-navigation-bar';
import * as ScreenOrientation from 'expo-screen-orientation';
import SuccessModal from "../modals/SuccessModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import ContestEntryModal from '../modals/ContestEntryModal';
import moment from 'moment';
import ResponsibleGaming from '../screens/Common/Responsible-Gaming';
import TransactionHistoryScreen from "../screens/TransactionHistoryScreen";
import EmailVerification from '../screens/Common/EmailVerification/Index';
import TermsAndConditionsScreen from "../screens/TermsAndConditionsScreen";
import PrivacyPolicyScreen from "../screens/PrivacyPolicyScreen";
import PromotionsScreen from "../screens/PromotionsScreen";
import SelfExclusionScreen from "../screens/SelfExclusionScreen";
import NotificationsScreen from "../screens/NotificationsScreen";
import PromosScreen from '../screens/User/promos';
import TournamentsListingScreen from '../screens/Tournaments/TournamentsListingScreen';
import TournamentDetails from "../screens/TournamentDetails";
import SignUpScreen from "../screens/Auth/SignUp";
import FriendsScreen from '../screens/User/friends/FriendsScreen';
import CompletedFriendChallenges from "../screens/CompletedFriendChallenges";
import AllTournaments from "../screens/AllTournaments";
import {BrandName, isFullSwing, Version} from "../constants";
import TV from "../screens/TV";
import PartnerAuth from "./PartnerAuth";
import EvenPlayAuth from "./EvenPlayAuth";
import SatelliteTournaments from "../screens/SatelliteTournaments";
import LevelSatellitesScreen from "../screens/LevelSatellitesScreen";
import useLiveSessionStream from "../hooks/useLiveSessionStream";
import {PlayerStateReason} from "../../generated/bay_session_pb";
import ActivePlayerConfirmationModal from "../modals/ActivePlayerConfirmationModal";
import {AutoPlayBalanceStatus} from "../../generated/smg_service_pb";
import FullSwingInfoModal from "../modals/FullSwingInfoModal";
import MessageOutlet, {hasConfirmationMessage} from "./MessageOutlet";
import {formatPayout} from "../utils";
import {projectBayPlayerState, userSessionDataEquals} from '../state/bayPlayerProjection';
import {balanceFromPlayerFunds} from "../utils/balanceFromPlayerFunds";
import {confirmationPromptOwed} from "../utils/confirmationPromptOwed";
import type {Message} from "../../generated/notification_pb";
import useActivePlayerConfirmationFlow from '../hooks/useActivePlayerConfirmationFlow';
import ServiceClient from '../utils/ServiceClient';
import {markRemovedFromCurrentPartnerSession} from '../utils/partnerRemovalGate';
import {PartnerKioskUpdate_UserSessionUpdateData_UserSessionState} from '../../generated/partner_service_pb';

const Stack = createNativeStackNavigator();
const EMPTY_ACTIVE_MESSAGES: readonly Message[] = [];

const DarkTheme: Theme = {
  dark: true,
  colors: {
    primary: 'rgb(10, 132, 255)', background: 'transparent', card: 'rgb(18, 18, 18)', text: 'rgb(229, 229, 231)',
    border: 'rgb(39, 39, 41)', notification: 'rgb(255, 69, 58)'
  }
};

const config = {
  screens: {
    challenges: 'challenges', account: 'account', promos: 'promos', Splash: 'splash', Register: 'register',
    LoginWithPin: 'login-with-pin', ResponsibleGaming: 'responsiblegaming', TransactionHistory: 'transaction-history',
    EmailVerification: 'confirm-email', TermsAndConditions: 'termsandconditions', PrivacyPolicy: 'privacy',
    Promotions: 'promotions', SelfExclusion: 'self-exclusion', Notifications: 'notifications',
    tournaments: 'tournaments',
    TournamentDetails: {
      path: 'tournament', parse: {
        id: (id: string) => Number(id), isPrivateTournament: (v: string) => v === 'true',
        showPopup: (v: string) => v === 'true'
      }
    },
    FriendsScreen: 'play-with-friends', CompletedFriendChallenges: 'completed-friend-challenges',
    AllTournaments: 'all-tournaments', TV: 'tv', PartnerAuth: 'partner-auth', TournamentSeries: 'tournament-series',
    LevelSatellites: 'level-satellites', EvenPlayAuth: 'evenplay-auth'
  }
};

const linking = {prefixes: [], config};

export const Main = () => {
  const {
    loadingOverlay, cancelModalConfig, isSuccessMessageShow, contestEntryModalConfig, session, user, setUserSessionData,
    setConfirmationBypassEnabled, applyBayPlayerProjection, markBayPlayerProjectionError, mergePlayerStreamProjections,
    setStreamError, setOnLine, sessionBackend, confirmBeforeEachTurnEnabled, setConfirmBeforeEachTurnEnabled,
    bayActiveBetCurrency, setBalance, setServerMovedToPointsInsufficientFunds, setInsufficientFundsNoFallback
  } = useContext(StoreContext);
  const {width} = useWindowDimensions();
  const navigationRef = useNavigationContainerRef();
  const routeNameRef = useRef<string | undefined>();
  const [enableStream, setEnableStream] = useState(false);
  // Dual-backend facade: `update` is the canonical UserSessionUpdateData (both
  // backends) driving the common session view; `bayPlayerState` is the raw bay
  // frame (undefined on legacy) driving the bay-only behaviors the canonical
  // shape can't carry (balance, station/bet bridges, confirmation modal,
  // on_turn, autoplay/free-shot/skill-index, reason dispatch).
  const {update, bayPlayerState, legacyConfirmationEvent, streamError} = useLiveSessionStream({enabled: enableStream});
  const confirmationFlow = useActivePlayerConfirmationFlow({
    bayPlayerState,
    legacyConfirmationEvent,
    legacySessionId: legacyConfirmationEvent?.sessionId ?? update?.sessionId,
  });

  const prevStatusRef = useRef<AutoPlayBalanceStatus | undefined>();
  const lastShownDownshiftAmountRef = useRef<number>();
  const isFirstRenderRef = useRef(true);
  const lastLegacyUserSessionDataRef = useRef<ReturnType<typeof useLiveSessionStream>['update']>();
  // SMG-4069: previous bay stationId, for detecting the seated→unseated edge
  // that arms the partner removal gate.
  const prevBayStationIdRef = useRef<number | undefined>();
  const legacyConfirmReadRef = useRef({key: '', status: 'idle' as 'idle' | 'loading' | 'resolved'});
  // SMG-3250: last balance (minor units) pushed from the stream, so the
  // per-frame funds→balance refresh dedupes steady-state frames. null =
  // unknown (the one-shot getBalance still seeds the initial value at login).
  const lastBalanceRef = useRef<number | null>(null);

  const [infoModal, setInfoModal] = useState({visible: false, title: '', subtitle: '', errorIcon: false});
  // Main owns resolved message visibility because it also arbitrates sibling modals.
  const [dismissedMessageIds, setDismissedMessageIds] = useState<ReadonlySet<string>>(() => new Set());
  const messageIdentityKey = `${session?.userId ?? 0n}:${session?.sessionStart ?? 0n}`;
  const messageIdentityKeyRef = useRef(messageIdentityKey);
  const activeMessages = bayPlayerState?.state?.activeMessages ?? EMPTY_ACTIVE_MESSAGES;
  const visibleMessages = useMemo(
    () => activeMessages.filter(message => !dismissedMessageIds.has(message.messageId)),
    [activeMessages, dismissedMessageIds]
  );
  const dismissMessage = useCallback((messageId: string) => {
    setDismissedMessageIds(current => {
      if (current.has(messageId)) return current;
      const next = new Set(current);
      next.add(messageId);
      return next;
    });
  }, []);

  useEffect(() => {
    if (messageIdentityKeyRef.current === messageIdentityKey) return;
    messageIdentityKeyRef.current = messageIdentityKey;
    setDismissedMessageIds(new Set());
  }, [messageIdentityKey]);

  useEffect(() => {
    const currentStatus = user?.autoPlayBalanceStatus;
    const currentAmount = user?.autoPlayAmount;

    if (currentStatus === undefined || currentAmount === undefined) return;

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      // Baseline prevStatus = BALANCE_OK instead of swallowing the
      // first observed value. The original guard (b4accf06) was added
      // to suppress a spurious popup when no prior state was observed,
      // but conflated that with "first observed value is OK." Pre-
      // SMG-2780 the engine never reset autoPlayBalanceStatus back to
      // BALANCE_OK, so a user who downshifted once would inherit
      // DOWNSHIFTED state on every subsequent session — and the
      // swallow meant the popup never fired for those sessions.
      // Baselining to OK lets the regular becameDownshifted check
      // fall through and fire on first DOWNSHIFTED render. The
      // SMG-2780 server fix also resets the persisted status after a
      // clean accept, so the typical case (user signs in with status
      // already reset to OK) avoids the gate entirely.
      prevStatusRef.current = AutoPlayBalanceStatus.BALANCE_OK;
      lastShownDownshiftAmountRef.current = currentAmount;
    }

    const becameDownshifted = prevStatusRef.current !== AutoPlayBalanceStatus.BALANCE_DOWNSHIFTED && currentStatus === AutoPlayBalanceStatus.BALANCE_DOWNSHIFTED;

    const isStillDownshifted = currentStatus === AutoPlayBalanceStatus.BALANCE_DOWNSHIFTED;

    const amountChanged = lastShownDownshiftAmountRef.current !== currentAmount;

    if (becameDownshifted || (isStillDownshifted && amountChanged)) {
      const formattedAmount = formatPayout(currentAmount / 100);

      setInfoModal({
        visible: true, title: 'Shot Amount Updated', errorIcon: false,
        subtitle: `We adjusted your shot to ${formattedAmount} based on your available balance.`
      });

      lastShownDownshiftAmountRef.current = currentAmount;
    }

    prevStatusRef.current = currentStatus;
  }, [user?.autoPlayBalanceStatus, user?.autoPlayAmount]);

  // The bay stream owns one coherent projection. Pure snapshot construction
  // stays separate from event side effects (modal edges, balance and banners),
  // and Provider applies the snapshot through one reducer action.
  useEffect(() => {
    const ps = bayPlayerState;

    // Legacy carries only the canonical session view. Keep its compatibility
    // path independent from the bay projection owner.
    if (!ps) {
      if (update && !userSessionDataEquals(lastLegacyUserSessionDataRef.current, update)) {
        lastLegacyUserSessionDataRef.current = update;
        setUserSessionData(update);
        setConfirmationBypassEnabled(update.accountSecurityBypassEnabled);
      }
      return;
    }

    if (ps.heartbeat) return;

    const nextProjection = projectBayPlayerState(ps.state, bayActiveBetCurrency);
    if (!nextProjection) return;

    applyBayPlayerProjection(nextProjection);

    // SMG-4069: an involuntary unseat observed on the stream arms the removal
    // gate — the deliberate departure paths (logout, checkout, accepted
    // Rejoin prompt) all clear it themselves immediately after. Persisting it
    // lets PartnerAuth refuse the SILENT auto-rejoin that the next background
    // document reload of /partner-auth?id_token=... would otherwise perform.
    //
    // TWO triggers, because the server publishes two unseat shapes:
    //   - Removals and roster clears: a frame with current_station_id=0, seen
    //     here as a seated→unseated stationId transition.
    //   - Idle evictions: the PLAYER_IDLE_EJECTED frame carries the REAL
    //     station id of the bay the player was ejected from, so the
    //     transition edge never fires for them — the reason field is the only
    //     signal. (Review finding: without this branch, idle-evicted players
    //     were still silently re-seated by the next reload.)
    const prevStationId = prevBayStationIdRef.current;
    prevBayStationIdRef.current = nextProjection.stationId;
    if (ps.reason === PlayerStateReason.PLAYER_IDLE_EJECTED
      || (prevStationId !== undefined && prevStationId > 0 && nextProjection.stationId <= 0)) {
      markRemovedFromCurrentPartnerSession().catch(() => {});
    }

    // Wallet balance is shared account state, so it remains an independent
    // projection with its own value dedupe.
    const nextBalance = balanceFromPlayerFunds(ps.state?.funds);
    if (nextBalance !== null && nextBalance !== lastBalanceRef.current) {
      lastBalanceRef.current = nextBalance;
      setBalance(nextBalance);
    }

    // User/engine enrichment changes independently from the canonical session
    // view. Its Context owners perform field-level value dedupe, so every
    // authoritative frame must reach them.
    mergePlayerStreamProjections(ps.state);

    // Funding reasons are transient event side effects. They intentionally do
    // not live in the snapshot reducer.
    const hasActiveBet = nextProjection.activeBetId !== '' && nextProjection.activeBetId !== '0';
    if (ps.reason === PlayerStateReason.PLAYER_MOVED_TO_POINTS_INSUFFICIENT_FUNDS) {
      setServerMovedToPointsInsufficientFunds(true);
      setInsufficientFundsNoFallback(false);

    } else if (ps.reason === PlayerStateReason.PLAYER_INSUFFICIENT_FUNDS) {
      setInsufficientFundsNoFallback(true);
      setServerMovedToPointsInsufficientFunds(false);

    } else if (hasActiveBet) {
      setServerMovedToPointsInsufficientFunds(false);
      setInsufficientFundsNoFallback(false);
    }
  }, [update, bayPlayerState, bayActiveBetCurrency, applyBayPlayerProjection, setUserSessionData, setConfirmationBypassEnabled, setBalance, mergePlayerStreamProjections, setServerMovedToPointsInsufficientFunds, setInsufficientFundsNoFallback]);

  useEffect(() => {
    if (sessionBackend !== 'legacy' || !session?.token || !update || update.userSessionState === PartnerKioskUpdate_UserSessionUpdateData_UserSessionState.NOT_JOINED) {
      legacyConfirmReadRef.current = {key: '', status: 'idle'};
      return;
    }

    const key = `${session.token}:${update.sessionId}`;
    const current = legacyConfirmReadRef.current;
    if (current.key === key && current.status !== 'idle') return;

    const pendingRead = {key, status: 'loading' as const};
    legacyConfirmReadRef.current = pendingRead;

    ServiceClient.getLegacyConfirmBeforeEachTurn(session.token).then(enabled => {
      if (legacyConfirmReadRef.current !== pendingRead) return;
      legacyConfirmReadRef.current = {key, status: 'resolved'};
      if (confirmBeforeEachTurnEnabled !== enabled) setConfirmBeforeEachTurnEnabled(enabled);
    }).catch(error => {
      if (legacyConfirmReadRef.current !== pendingRead) return;
      legacyConfirmReadRef.current = {key, status: 'idle'};
      console.error('[Main] failed to hydrate legacy confirm-before-each-turn', error);
    });
  }, [sessionBackend, session?.token, update, confirmBeforeEachTurnEnabled, setConfirmBeforeEachTurnEnabled]);

  useEffect(() => {
    if (streamError) {
      setStreamError(streamError);
      markBayPlayerProjectionError();
    }
  }, [streamError, setStreamError, markBayPlayerProjectionError]);

  useEffect(() => {
    // bay_session.streamPlayerState is universal — every signed-in mobile
    // user gets a per-user channel regardless of partner mode. The legacy
    // `isFullSwing` gate is intentionally dropped here; the flag remains
    // in scope for other partner-specific UI behavior (PartnerAuth routing,
    // FullSwing-only modal copy, etc.) but no longer gates the stream
    // subscription.
    setEnableStream(!!session);
  }, [session]);

  useLayoutEffect(() => {
    console.log(Version);

    if (Platform.OS === 'web') {
      const handleClick = () => {
        void AsyncStorage.setItem('lastActiveTime', moment().format());
      };

      const handleOnline = () => setOnLine(true);
      const handleOffline = () => setOnLine(false);

      document.addEventListener('click', handleClick, true);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        document.removeEventListener('click', handleClick, true);
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };

    } else {
      void NavigationBar.setButtonStyleAsync('light');
      void NavigationBar.setBackgroundColorAsync('black');

      if (width < 768) {
        void ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT);
      }
    }
  }, []);

  const shouldRedirectToPartnerAuth = (() => {
    if (Platform.OS !== 'web' || !isFullSwing) return false;

    const {pathname, search} = window.location;
    const params = new URLSearchParams(search);

    const hasPartnerAuthParams = params.has('id_token') || params.has('session_id') || params.has('sub_id');

    const isRootPath = pathname === '/' || pathname === '';
    const isPartnerAuthPath = pathname === '/partner-auth';

    return isRootPath && hasPartnerAuthParams && !isPartnerAuthPath;
  })();

  if (shouldRedirectToPartnerAuth) {
    window.location.replace(`/partner-auth${window.location.search}`);
    return null;
  }

  // Non-partner-skin analog of shouldRedirectToPartnerAuth: a root-path
  // load carrying a session_id (native evenplay deep link / manual bay-code
  // QR) routes to the EvenPlayAuth stub instead of falling through to
  // Splash. Gated on !isFullSwing so the two redirects can never both match
  // — isFullSwing selects exactly one of the two skins.
  const shouldRedirectToEvenPlayAuth = (() => {
    if (Platform.OS !== 'web' || isFullSwing) return false;

    const {pathname, search} = window.location;
    const params = new URLSearchParams(search);

    const hasEvenPlayAuthParams = params.has('session_id');

    const isRootPath = pathname === '/' || pathname === '';
    const isEvenPlayAuthPath = pathname === '/evenplay-auth';

    return isRootPath && hasEvenPlayAuthParams && !isEvenPlayAuthPath;
  })();

  if (shouldRedirectToEvenPlayAuth) {
    window.location.replace(`/evenplay-auth${window.location.search}`);
    return null;
  }

  const gameplayConfirmationTakesPriority = confirmationFlow.visibleTimestamp > 0 || confirmationPromptOwed(bayPlayerState?.state);
  const notificationConfirmationPresent = hasConfirmationMessage(visibleMessages);
  const existingBlockingUiPresent = !!cancelModalConfig?.visible || !!isSuccessMessageShow || !!contestEntryModalConfig?.visible || loadingOverlay;
  const showNotificationConfirmation = notificationConfirmationPresent && !gameplayConfirmationTakesPriority && !existingBlockingUiPresent;
  const higherPriorityModalPresent = gameplayConfirmationTakesPriority || notificationConfirmationPresent || existingBlockingUiPresent;

  return (
    <>
      <StatusBar backgroundColor="black"/>

      <NavigationContainer ref={navigationRef} theme={DarkTheme} linking={linking}
                           onReady={() => {
                             routeNameRef.current = navigationRef.getCurrentRoute()?.name;
                           }}
                           onStateChange={() => {
                             const previousRouteName = routeNameRef.current;
                             const currentRouteName = navigationRef.getCurrentRoute()?.name;

                             if (currentRouteName !== undefined && previousRouteName !== currentRouteName) {
                               track('Screen View', {screen: currentRouteName});
                             }

                             if (currentRouteName !== undefined) {
                               routeNameRef.current = currentRouteName;
                             }
                           }}>
        <Stack.Navigator screenOptions={{headerShown: false, title: BrandName}}>
          <Stack.Screen name="challenges" component={BetSlip}/>
          <Stack.Screen name="account" component={Account}/>
          <Stack.Screen name="promos" component={PromosScreen}/>
          <Stack.Screen name="Splash" component={SplashScreen}/>
          <Stack.Screen name="Register" component={SignUpScreen}/>
          <Stack.Screen name="LoginWithPin" component={LoginWithPin}/>
          <Stack.Screen name="ResponsibleGaming" component={ResponsibleGaming}/>
          <Stack.Screen name="TransactionHistory" component={TransactionHistoryScreen}/>
          <Stack.Screen name="EmailVerification" component={EmailVerification}/>
          <Stack.Screen name="TermsAndConditions" component={TermsAndConditionsScreen}/>
          <Stack.Screen name="PrivacyPolicy" component={PrivacyPolicyScreen}/>
          <Stack.Screen name="Promotions" component={PromotionsScreen}/>
          <Stack.Screen name="SelfExclusion" component={SelfExclusionScreen}/>
          <Stack.Screen name="Notifications" component={NotificationsScreen}/>
          <Stack.Screen name="tournaments" component={TournamentsListingScreen}/>
          <Stack.Screen name="TournamentDetails" component={TournamentDetails}/>
          <Stack.Screen name="FriendsScreen" component={FriendsScreen}/>
          <Stack.Screen name="CompletedFriendChallenges" component={CompletedFriendChallenges}/>
          <Stack.Screen name="AllTournaments" component={AllTournaments}/>
          <Stack.Screen name="TV" component={TV}/>
          <Stack.Screen name="PartnerAuth" component={PartnerAuth}/>
          <Stack.Screen name="EvenPlayAuth" component={EvenPlayAuth}/>
          <Stack.Screen name="TournamentSeries" component={SatelliteTournaments}/>
          <Stack.Screen name="LevelSatellites" component={LevelSatellitesScreen}/>
        </Stack.Navigator>
      </NavigationContainer>

      <ActivePlayerConfirmationModal
        visible={confirmationFlow.visibleTimestamp > 0}
        isResponding={confirmationFlow.isSubmitting}
        readinessBlockedMessage={confirmationFlow.readinessBlockedMessage}
        onConfirm={() => void confirmationFlow.confirm()}
        onRequestClose={confirmationFlow.requestClose}
      />

      <FullSwingInfoModal
        // SMG-3884 modal arbitration: React Native presents only ONE <Modal>
        // at a time. At a rotation turn boundary with confirm-on-turn, the
        // "Shot Amount Updated" downshift popup and the "You're Up Next!"
        // ActivePlayerConfirmationModal can flip visible on the same frame.
        // The confirmation modal is the SOLE trigger for RespondToConfirmation
        // (which clears awaiting_confirmation and arms the shot), so it MUST
        // win — if RN suppressed it the player would wedge permanently. The
        // downshift popup DEFERS while the confirmation modal is up (its
        // infoModal state is untouched) and re-surfaces on the next frame once
        // the player confirms and the controller clears its visible projection.
        info={higherPriorityModalPresent ? {...infoModal, visible: false} : infoModal}
        onRequestClose={() => setInfoModal({visible: false, title: '', subtitle: '', errorIcon: false})}
      />

      <SuccessModal/>
      <CancelModal/>
      <ContestEntryModal/>
      <Loader visible={loadingOverlay}/>

      {/* In-bay messages are a read-side projection on the existing
          PlayerState stream. NotificationService remains the lifecycle owner;
          MessageOutlet only routes the current snapshot by message type. */}
      <MessageOutlet
        identityKey={messageIdentityKey} messages={visibleMessages} showConfirmation={showNotificationConfirmation}
        onDismiss={dismissMessage}
      />
    </>
  );
};
